import {
  useMutation,
  useQueryClient,
  queryOptions,
  type MutateOptions,
} from '@tanstack/react-query';
import { create } from 'zustand';
import { client } from '@blazeteams/sdk/client';
import { useAuthStore } from '@/store/auth-store';
import {
  sessionScope,
  sessionRevision,
  requireCurrentSession,
} from '@/features/auth/session-scope';
import { captureChatOwner, currentChatChannelsKey } from './creation-actions';
import { removeLeftChannel } from './leave-channel-cache';
import {
  ChannelActionError,
  managementProblem,
  readChannelAction,
  readOwnedChannelMembers,
  snapshotChannelAction,
  type ChannelOperation,
  type ChannelActionInput,
  type ChannelActionReceipt,
  type ChannelActionSnapshot,
} from './management-contract';
import { readChatPage, chatUUID } from './creation-contract';
import type { Channel } from './queries';

type Owner = ReturnType<typeof captureChatOwner>;
type Action = {
  owner: Owner;
  id: number;
  input: ChannelActionSnapshot;
  phase: 'pending' | 'uncertain' | 'reviewing' | 'reviewed';
  review?: { channel?: Channel; available: boolean };
  failed?: boolean;
};
export const useChannelManagementState = create<{
  actions: Record<string, Action>;
}>(() => ({ actions: {} }));
useAuthStore.subscribe((next, previous) => {
  if (sessionScope(next) !== sessionScope(previous))
    useChannelManagementState.setState({ actions: {} });
});
let sequence = 0;
const credentials = new WeakMap<object, string>();
function current(owner: Owner, signal?: AbortSignal) {
  requireCurrentSession(owner.revision);
  if (signal?.aborted) throw new DOMException('Aborted', 'AbortError');
}
function ownAction(id: string, owner: Owner) {
  const a = useChannelManagementState.getState().actions[id];
  return a?.owner.revision === owner.revision && a.owner.scope === owner.scope
    ? a
    : undefined;
}
function lease(a: Action) {
  current(a.owner);
  if (ownAction(a.input.channelId, a.owner)?.id !== a.id)
    throw new DOMException('Aborted', 'AbortError');
}
function setAction(id: string, value: Action | undefined) {
  useChannelManagementState.setState((s) => {
    const actions = { ...s.actions };
    if (value) actions[id] = value;
    else delete actions[id];
    return { actions };
  });
}
function authorization(intent: object) {
  const token = useAuthStore.getState().token;
  if (!token) throw new ChannelActionError('unauthorized', false);
  credentials.set(
    intent,
    token.startsWith('Bearer ') ? token : 'Bearer ' + token,
  );
}
export function currentChatMembersKey(id: string) {
  const owner = captureChatOwner();
  return [
    'team-chat',
    'members',
    id,
    'owner',
    owner.scope,
    owner.revision,
  ] as const;
}
export function ownedChatMembers(id: string) {
  const owner = captureChatOwner();
  return queryOptions({
    queryKey: currentChatMembersKey(id),
    enabled: !!id && owner.scope !== 'anonymous',
    retry: false,
    queryFn: async ({ signal }) => {
      current(owner, signal);
      let response;
      try {
        response = await client.get<unknown>({
          url: '/channels/{id}/members',
          path: { id },
          signal,
          headers: { 'Accept-Language': owner.language },
        });
      } catch {
        current(owner, signal);
        throw new ChannelActionError('members_failed', false);
      }
      current(owner, signal);
      if (response.error || response.response?.status !== 200)
        throw new ChannelActionError('members_failed', false);
      return readOwnedChannelMembers(response.data, owner, id);
    },
  });
}
export function useManagedChannelAction<V>(
  operation: ChannelOperation,
  prepare: (value: V) => ChannelActionInput,
) {
  const qc = useQueryClient();
  const mutation = useMutation({
    mutationKey: ['team-chat', 'management', operation],
    retry: false,
    gcTime: 0,
    mutationFn: async (intent: Action) => {
      lease(intent);
      const auth = credentials.get(intent);
      if (!auth) throw new ChannelActionError('unauthorized', false);
      const input = intent.input,
        op = input.operation;
      const headers = {
        Authorization: auth,
        'Accept-Language': intent.owner.language,
      };
      let response;
      try {
        if (op === 'update')
          response = await client.put<unknown>({
            url: '/channels/{id}',
            path: { id: input.channelId },
            body: input.patch,
            headers,
          });
        else if (op === 'delete')
          response = await client.delete<unknown>({
            url: '/channels/{id}',
            path: { id: input.channelId },
            headers,
          });
        else if (op === 'remove_member')
          response = await client.delete<unknown>({
            url: '/channels/{id}/members/{userId}',
            path: { id: input.channelId, userId: input.userId! },
            headers,
          });
        else if (op === 'add_members')
          response = await client.post<unknown>({
            url: '/channels/{id}/members',
            path: { id: input.channelId },
            body: { user_ids: input.userIds },
            headers,
          });
        else
          response = await client.post<unknown>({
            url: '/channels/{id}/' + op,
            path: { id: input.channelId },
            headers,
          });
      } catch {
        lease(intent);
        throw new ChannelActionError();
      }
      lease(intent);
      if (response.error || response.response?.status !== 200)
        throw managementProblem(response.error, response.response?.status ?? 0);
      return readChannelAction(response.data, intent.owner, input);
    },
  });
  const mutateAsync = async (value: V): Promise<ChannelActionReceipt> => {
    const owner = captureChatOwner();
    current(owner);
    if (!chatUUID(owner.user) || !chatUUID(owner.tenant))
      throw new ChannelActionError('unauthorized', false);
    const input = snapshotChannelAction(operation, prepare(value));
    if (ownAction(input.channelId, owner))
      throw new ChannelActionError('busy', false);
    const intent: Action = { owner, id: ++sequence, input, phase: 'pending' };
    authorization(intent);
    setAction(input.channelId, intent);
    try {
      const result = await mutation.mutateAsync(intent);
      lease(intent);
      if (result.deleted || result.left) {
        await removeLeftChannel(qc, input.channelId);
        lease(intent);
      } else if (result.channel) {
        for (const [key, rows] of qc.getQueriesData<Channel[]>({
          queryKey: currentChatChannelsKey(),
        })) {
          if (!rows) continue;
          const next = rows.map((row) =>
            row.id === input.channelId ? result.channel! : row,
          );
          if (
            input.operation === 'unarchive' &&
            !next.some((row) => row.id === input.channelId)
          )
            next.push(result.channel);
          const includeArchived =
            (key.at(-1) as { archived?: boolean } | undefined)?.archived ===
            true;
          qc.setQueryData(
            key,
            includeArchived ? next : next.filter((row) => !row.is_archived),
          );
        }
        qc.setQueryData(
          currentChatMembersKey(input.channelId),
          result.channel.members,
        );
      }
      lease(intent);
      setAction(input.channelId, undefined);
      void qc.invalidateQueries({ queryKey: currentChatChannelsKey() });
      if (!result.deleted && !result.left)
        void qc.invalidateQueries({
          queryKey: currentChatMembersKey(input.channelId),
        });
      return result;
    } catch (error) {
      if (
        owner.revision === sessionRevision() &&
        ownAction(input.channelId, owner)?.id === intent.id
      )
        setAction(
          input.channelId,
          error instanceof ChannelActionError && error.uncertain
            ? { ...intent, phase: 'uncertain' }
            : undefined,
        );
      throw error;
    } finally {
      credentials.delete(intent);
    }
  };
  const mutate = (
    value: V,
    options?: MutateOptions<ChannelActionReceipt, unknown, V, unknown>,
  ) => {
    const revision = sessionRevision(),
      context = {
        client: qc,
        mutationKey: ['team-chat', 'management', operation],
        meta: undefined,
      };
    void mutateAsync(value).then(
      (result) => {
        if (revision === sessionRevision()) {
          options?.onSuccess?.(result, value, undefined, context);
          options?.onSettled?.(result, null, value, undefined, context);
        }
      },
      (error) => {
        if (revision === sessionRevision()) {
          options?.onError?.(error, value, undefined, context);
          options?.onSettled?.(undefined, error, value, undefined, context);
        }
      },
    );
  };
  return { ...mutation, mutate, mutateAsync };
}
export async function reviewChannelManagement(id: string) {
  const owner = captureChatOwner(),
    action = ownAction(id, owner);
  if (!action || !['uncertain', 'reviewed'].includes(action.phase))
    throw new ChannelActionError('busy', false);
  const request = {};
  authorization(request);
  setAction(id, { ...action, phase: 'reviewing', failed: false });
  try {
    const response = await client.get<unknown>({
      url: '/channels/{id}',
      path: { id },
      headers: {
        Authorization: credentials.get(request)!,
        'Accept-Language': owner.language,
      },
    });
    lease(action);
    const problem = managementProblem(
      response.error,
      response.response?.status ?? 0,
    );
    let review: NonNullable<Action['review']>;
    if (
      response.response?.status === 403 &&
      problem.key === 'admin.problem.denied' &&
      !problem.uncertain
    )
      review = { available: false };
    else {
      if (response.error || response.response?.status !== 200)
        throw new ChannelActionError('review_failed', false);
      const raw = response.data as { success?: boolean; data?: unknown };
      const row = readChatPage(
        { success: raw?.success, data: [raw?.data] },
        owner,
      )[0];
      if (row.id !== id) throw new ChannelActionError('review_failed', false);
      review = { available: true, channel: row };
    }
    lease(action);
    setAction(id, { ...action, phase: 'reviewed', review });
  } catch {
    if (
      owner.revision === sessionRevision() &&
      ownAction(id, owner)?.id === action.id
    )
      setAction(id, { ...action, phase: 'uncertain', failed: true });
    throw new ChannelActionError('review_failed', false);
  } finally {
    credentials.delete(request);
  }
}
export function acceptChannelManagementReview(id: string) {
  const a = ownAction(id, captureChatOwner());
  if (!a || a.phase !== 'reviewed') throw new ChannelActionError('busy', false);
  lease(a);
  setAction(id, undefined);
}
